import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { X, Plus, Send, Trash2, Building2, User, ExternalLink, Loader2, Check, AlertCircle, Sparkles, DollarSign } from "lucide-react";
import { toast } from "sonner";

const API_V1 = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const STATUS_COLORS = {
  pending: { bg: "bg-amber-500/15", text: "text-amber-300", border: "border-amber-500/30", label: "Pending" },
  intro_sent: { bg: "bg-blue-500/15", text: "text-blue-300", border: "border-blue-500/30", label: "Intro Sent" },
  contacted: { bg: "bg-purple-500/15", text: "text-purple-300", border: "border-purple-500/30", label: "Contacted" },
  signed_up: { bg: "bg-green-500/15", text: "text-green-300", border: "border-green-500/30", label: "Signed Up" },
  declined: { bg: "bg-rose-500/15", text: "text-rose-300", border: "border-rose-500/30", label: "Declined" },
};

/**
 * "Recommend a Business or Agent" panel — drop in the Referrals dashboard.
 * Owners can recommend a non-LocalBest contact; admin reviews + closes them.
 * Once admin links them to a paid listing AND toggles "Trusted Network visible",
 * the partner card appears on this owner's public profile.
 */
export default function RecommendationsPanel() {
  const { getAuthHeader } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, signed_up: 0, earned_unpaid: 0, paid_total: 0 });
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_V1}/recommendations/my`, { headers: getAuthHeader() });
      setItems(res.data.recommendations || []);
      setStats(res.data.stats || {});
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const sendIntro = async (id) => {
    try {
      await axios.post(`${API_V1}/recommendations/${id}/send-intro`, {}, { headers: getAuthHeader() });
      toast.success("Intro sent — our team will reach out shortly.");
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send intro.");
    }
  };

  const removeRec = async (id) => {
    if (!window.confirm("Remove this recommendation?")) return;
    try {
      await axios.delete(`${API_V1}/recommendations/${id}`, { headers: getAuthHeader() });
      toast.success("Removed.");
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove.");
    }
  };

  return (
    <div className="rounded-xl bg-[#0F172A] border border-white/10 p-5" data-testid="recommendations-panel">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-white font-semibold text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#A78BFA]" />
            Recommend Businesses & Agents
          </h2>
          <p className="text-xs text-[#94A3B8] mt-1 max-w-2xl">
            Recommend a business or agent you know, like, and trust. Once they sign up and admin approves them,
            they&apos;ll appear on your public listing&apos;s
            <strong className="text-white"> &quot;LocalBest Trusted Network™&quot;</strong> section.
            Earn <strong className="text-[#10B981]">20% of their first collected month</strong> ({" "}
            <strong className="text-[#10B981]">40%</strong> if you&apos;re a Certified Creator) plus{" "}
            <strong className="text-[#10B981]">10% recurring</strong> every month they stay active. Agent seats earn 10% recurring per seat.
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white text-sm font-semibold hover:opacity-90 transition shrink-0"
          data-testid="recommend-add-btn"
        >
          <Plus className="w-4 h-4" /> Recommend
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        <MiniStat label="Total" value={stats.total || 0} />
        <MiniStat label="Signed Up" value={stats.signed_up || 0} color="#10B981" />
        <MiniStat label="On Your Network" value={stats.active || 0} color="#7C3AED" />
        <MiniStat label="Earned (Unpaid)" value={`$${(stats.earned_unpaid || 0).toFixed(0)}`} color="#F59E0B" icon={DollarSign} />
      </div>

      {/* List */}
      {loading ? (
        <div className="h-24 bg-white/5 rounded-xl animate-pulse" />
      ) : items.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-white/10 rounded-lg">
          <p className="text-[#94A3B8] text-sm">No recommendations yet.</p>
          <p className="text-[#64748B] text-xs mt-1">Click <strong className="text-white">+ Recommend</strong> to get started.</p>
        </div>
      ) : (
        <div className="overflow-x-auto -mx-5 px-5">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#64748B] border-b border-white/5">
                <th className="py-2 pr-3">Name</th>
                <th className="py-2 pr-3">Type</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Fee</th>
                <th className="py-2 pr-3">On Listing</th>
                <th className="py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const sc = STATUS_COLORS[r.status] || STATUS_COLORS.pending;
                return (
                  <tr key={r.id} className="border-b border-white/5 last:border-0 align-top" data-testid={`rec-row-${r.id}`}>
                    <td className="py-3 pr-3">
                      <div className="flex items-start gap-2">
                        <div className="w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-xs text-white font-bold flex-shrink-0">
                          {r.recommendee_type === "business" ? <Building2 className="w-4 h-4 text-[#00E5FF]" /> : <User className="w-4 h-4 text-[#EC4899]" />}
                        </div>
                        <div className="min-w-0">
                          <p className="text-white font-medium truncate">{r.recommendee_name}</p>
                          {r.recommendee_email && (
                            <a href={`mailto:${r.recommendee_email}`} className="text-[#00E5FF] hover:underline text-[11px] truncate block" data-testid={`rec-email-${r.id}`}>
                              {r.recommendee_email}
                            </a>
                          )}
                          {r.recommendee_phone && (
                            <a href={`tel:${r.recommendee_phone}`} className="text-[#A78BFA] hover:underline text-[11px] truncate block" data-testid={`rec-phone-${r.id}`}>
                              {r.recommendee_phone}
                            </a>
                          )}
                          {(r.recommendee_city || r.recommendee_state || r.recommendee_category) && (
                            <p className="text-[#64748B] text-[10px] mt-0.5">
                              {[r.recommendee_category, [r.recommendee_city, r.recommendee_state].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-3 text-[#94A3B8] capitalize">{r.recommendee_type}</td>
                    <td className="py-3 pr-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] border ${sc.bg} ${sc.text} ${sc.border}`}>{sc.label}</span>
                    </td>
                    <td className="py-3 pr-3 text-[#FBBF24]">
                      {r.referral_fee_amount ? `$${r.referral_fee_amount}` : <span className="text-[#64748B] text-xs">—</span>}
                      {r.referral_fee_status === "earned" && <span className="ml-1 text-[10px] text-[#10B981]">earned</span>}
                      {r.referral_fee_status === "paid" && <span className="ml-1 text-[10px] text-[#10B981]">paid</span>}
                    </td>
                    <td className="py-3 pr-3">
                      {r.trusted_network_visible ? (
                        <span className="inline-flex items-center gap-1 text-[10px] text-[#10B981]"><Check className="w-3 h-3" /> Visible</span>
                      ) : (
                        <span className="text-[#64748B] text-[10px]">Hidden</span>
                      )}
                    </td>
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-1.5 justify-end flex-wrap">
                        {(r.status === "pending" || r.status === "intro_sent") && (
                          <button
                            onClick={() => sendIntro(r.id)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-[#7C3AED]/20 hover:bg-[#7C3AED]/30 border border-[#7C3AED]/40 text-[#A78BFA] text-[11px]"
                            title="Push them into our CRM and tee them up for outreach"
                            data-testid={`rec-send-intro-${r.id}`}
                          >
                            <Send className="w-3 h-3" /> {r.status === "intro_sent" ? "Resend" : "Send Intro"}
                          </button>
                        )}
                        {r.partner && (
                          <a
                            href={`/business/${r.partner.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[#00E5FF] hover:text-[#22D3EE] text-[11px]"
                            title="View their LocalBest listing"
                          >
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                        {(r.status === "pending" || r.status === "declined") && (
                          <button
                            onClick={() => removeRec(r.id)}
                            className="inline-flex items-center justify-center w-6 h-6 rounded-md hover:bg-red-500/20 text-red-400"
                            title="Remove"
                            data-testid={`rec-remove-${r.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AnimatePresence>
        {showAdd && <AddRecommendationModal onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); fetchAll(); }} getAuthHeader={getAuthHeader} />}
      </AnimatePresence>
    </div>
  );
}

function MiniStat({ label, value, color = "#7C3AED", icon: Icon }) {
  return (
    <div className="rounded-lg bg-white/5 border border-white/10 p-3 flex items-center gap-2">
      <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}22`, color }}>
        {Icon ? <Icon className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-wider text-[#64748B]">{label}</p>
        <p className="text-white font-bold text-base leading-tight">{value}</p>
      </div>
    </div>
  );
}

function AddRecommendationModal({ onClose, onCreated, getAuthHeader }) {
  const [form, setForm] = useState({
    recommendee_type: "business",
    recommendee_name: "",
    recommendee_email: "",
    recommendee_phone: "",
    recommendee_website: "",
    recommendee_category: "",
    recommendee_city: "",
    recommendee_state: "",
    endorsement_text: "",
    personal_note: "",
    send_intro_now: true,
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.recommendee_name.trim()) { toast.error("Add a name"); return; }
    if (!form.recommendee_email && !form.recommendee_phone) { toast.error("At least email or phone required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API_V1}/recommendations`, form, { headers: getAuthHeader() });
      toast.success(form.send_intro_now ? "Recommendation submitted — we'll reach out!" : "Saved.");
      onCreated();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit recommendation.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="recommend-modal"
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.97 }}
        className="bg-[#0F172A] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#0F172A] flex items-center justify-between p-5 border-b border-white/10 z-10">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#A78BFA]" /> Recommend a Business or Agent
          </h2>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-white p-1" data-testid="recommend-modal-close"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          {/* Type toggle */}
          <div className="grid grid-cols-2 gap-2">
            {[
              { v: "business", label: "Business", desc: "20% first month + 10% recurring", icon: Building2, color: "#00E5FF" },
              { v: "agent", label: "Agent / Individual", desc: "10% recurring per seat", icon: User, color: "#EC4899" },
            ].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => update("recommendee_type", opt.v)}
                className={`p-3 rounded-lg border-2 text-left transition ${form.recommendee_type === opt.v ? "border-[#7C3AED] bg-[#7C3AED]/10" : "border-white/10 bg-white/5 hover:border-white/20"}`}
                data-testid={`type-${opt.v}`}
              >
                <opt.icon className="w-4 h-4 mb-1.5" style={{ color: opt.color }} />
                <p className="text-white text-sm font-semibold">{opt.label}</p>
                <p className="text-[#94A3B8] text-[11px]">{opt.desc}</p>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Name *" value={form.recommendee_name} onChange={(v) => update("recommendee_name", v)} testid="rec-name" />
            <Field label="Category" value={form.recommendee_category} onChange={(v) => update("recommendee_category", v)} placeholder="e.g. HVAC, Realtor" testid="rec-category" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Email" type="email" value={form.recommendee_email} onChange={(v) => update("recommendee_email", v)} testid="rec-email" />
            <Field label="Phone" value={form.recommendee_phone} onChange={(v) => update("recommendee_phone", v)} testid="rec-phone" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Website" value={form.recommendee_website} onChange={(v) => update("recommendee_website", v)} placeholder="https://" testid="rec-website" />
            <div className="grid grid-cols-2 gap-2">
              <Field label="City" value={form.recommendee_city} onChange={(v) => update("recommendee_city", v)} testid="rec-city" />
              <Field label="State" value={form.recommendee_state} onChange={(v) => update("recommendee_state", v)} testid="rec-state" maxLength={2} />
            </div>
          </div>
          <div>
            <label className="block text-xs text-[#94A3B8] mb-1.5">Public endorsement quote <span className="text-[#64748B]">(optional, max 280)</span></label>
            <textarea
              value={form.endorsement_text}
              onChange={(e) => update("endorsement_text", e.target.value)}
              maxLength={280}
              rows={2}
              placeholder="e.g. I've referred clients to them for 5 years — top-tier service."
              className="w-full bg-[#020817] border border-white/10 text-white rounded-md px-3 py-2 text-sm"
              data-testid="rec-endorsement"
            />
            <p className="text-[10px] text-[#64748B] mt-1">Shown publicly on your listing once admin approves.</p>
          </div>
          <div>
            <label className="block text-xs text-[#94A3B8] mb-1.5">Personal note to LocalBest team <span className="text-[#64748B]">(private)</span></label>
            <textarea
              value={form.personal_note}
              onChange={(e) => update("personal_note", e.target.value)}
              maxLength={1000}
              rows={2}
              placeholder="Anything that'll help us close them — context, timing, who's their current vendor, etc."
              className="w-full bg-[#020817] border border-white/10 text-white rounded-md px-3 py-2 text-sm"
              data-testid="rec-personal-note"
            />
          </div>
          <label className="flex items-start gap-2 p-3 rounded-lg bg-[#7C3AED]/10 border border-[#7C3AED]/30 cursor-pointer" data-testid="rec-send-intro-toggle-label">
            <input
              type="checkbox"
              checked={form.send_intro_now}
              onChange={(e) => update("send_intro_now", e.target.checked)}
              className="mt-0.5 accent-[#7C3AED]"
              data-testid="rec-send-intro-toggle"
            />
            <div className="text-xs text-[#E2E8F0]">
              <strong className="text-white">Send intro to them now</strong> — they'll be tee'd up before our team reaches out.
              Leave unchecked to save quietly for your reference.
            </div>
          </label>

          <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-3 flex items-start gap-2 text-xs text-amber-200">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <p>
              <strong>First-to-recommend wins.</strong> If another LocalBest member already recommended this contact, you&apos;ll see an error.
              Once they activate and pay, you earn{" "}
              <strong>{form.recommendee_type === "business" ? "20% of their first month (40% if Certified) + 10% every month they stay" : "10% recurring per agent seat while active"}</strong>.
              Paid monthly on collected payments after a 30-day hold.
            </p>
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg border border-white/20 text-white hover:bg-white/5 transition text-sm">Cancel</button>
            <button type="submit" disabled={saving} className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#EC4899] text-white text-sm font-semibold hover:opacity-90 transition disabled:opacity-50" data-testid="recommend-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {saving ? "Submitting…" : "Submit Recommendation"}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, maxLength, testid }) {
  return (
    <div>
      <label className="block text-xs text-[#94A3B8] mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        maxLength={maxLength}
        className="w-full bg-[#020817] border border-white/10 text-white rounded-md px-3 py-2 text-sm"
        data-testid={testid}
      />
    </div>
  );
}
